import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import center from '@turf/center';
import Map from '../components/Map';
import Timeline from '../components/Timeline';
import Sidebar from '../components/Sidebar';
import Button from '../components/Button';
import UserSelectModal from '../components/UserSelectModal';
import AgentDetailsPanel from '../components/AgentDetailsPanel';
import LiveAgentDetailsPanel from '../components/LiveAgentDetailsPanel';
import VirtualAgentsPanel from '../components/VirtualAgentsPanel';
import LiveAgentsPanel from '../components/LiveAgentsPanel';
import { fetchScenario, updateScenario } from '../services/lvcService';
import { fetchUserProfiles } from '../services/usersService';
import { fetchSkiAreaById } from '../services/skiAreasService';
import { getProfilePictureUrl } from '../services/storageService';
import { initializeAgentStates, calculateMaxSimulationTime } from '../services/simulationEngine';
import { createSimulationClock, stepAgents } from '../services/simulationClock';
import { preloadBehaviors, clearDatasetCache } from '../services/datasetCache';
import { initialize as initializeInterface, onVirtualAgentTick, createSkierDataModel, cleanup as cleanupInterface, setErrorCallback } from '../services/interface';
import { uploadTick, subscribeLiveAgent, readSkiGroups, moveAgentsToGroup } from '../services/lvcFirestore';
import { getSkiGroupDetails } from '../services/skiGroupsService';
import SkiGroupJoinPrompt from '../components/SkiGroupJoinPrompt';
import { useError } from '../contexts/ErrorContext';
import './LVCScenarioPage.css';

const LVCScenarioPage = () => {
  const navigate = useNavigate();
  const { scenarioId } = useParams();
  const { addError } = useError();
  const [activeTab, setActiveTab] = useState(null); // No tab active by default
  const [sidebarWidth, setSidebarWidth] = useState(400);
  const [virtualAgents, setVirtualAgents] = useState([]);
  const [liveAgents, setLiveAgents] = useState([]);
  const [showUserSelect, setShowUserSelect] = useState(false);
  const [selectingFor, setSelectingFor] = useState(null); // 'virtual' or 'live'
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [simulationRunning, setSimulationRunning] = useState(false);
  const [simulationPaused, setSimulationPaused] = useState(false);
  // True once datasets are preloaded and the first positions exist. The clock must not start
  // before this, or it captures an empty agent list and never advances.
  const [simulationReady, setSimulationReady] = useState(false);
  // Asked once on Run when a live skier is already in a ski group: { candidates, agentCount }.
  const [skiGroupPrompt, setSkiGroupPrompt] = useState(null);
  const [compilerError, setCompilerError] = useState(null);
  const [simulationStartTime, setSimulationStartTime] = useState(null);
  const [simulationCurrentTime, setSimulationCurrentTime] = useState(0);
  const [agentStates, setAgentStates] = useState([]);
  const [selectedAgentIndex, setSelectedAgentIndex] = useState(null);
  const [uploadEnabled, setUploadEnabled] = useState(false);
  const [downloadEnabled, setDownloadEnabled] = useState(false);
  const [liveAgentPositions, setLiveAgentPositions] = useState({}); // Map of uid -> position data (derived from SkierDataModel)
  const [liveAgentHistory, setLiveAgentHistory] = useState({}); // Map of uid -> array of telemetry history
  const [selectedLiveAgentUid, setSelectedLiveAgentUid] = useState(null); // Selected live agent UID
  const [detectedFeatures, setDetectedFeatures] = useState({}); // Map of uid -> detectedFeature { type, id, name, color? }
  const [headerHeight, setHeaderHeight] = useState(50); // Default header height
  const [virtualAgentProfilePictures, setVirtualAgentProfilePictures] = useState(() => new window.Map()); // Map of uid -> profile picture URL
  const [failedProfilePictures, setFailedProfilePictures] = useState(new Set()); // Set of uids with failed profile pictures
  const [skiAreaId, setSkiAreaId] = useState(null); // scenario.ski_area_id (source of truth)
  const [skiArea, setSkiArea] = useState(null); // Current ski area for the scenario
  const [openAgentMenu, setOpenAgentMenu] = useState(null); // Which agent's menu is open (index or null)
  const [swappingAgentIndex, setSwappingAgentIndex] = useState(null); // Index of agent being swapped
  const headerRef = useRef(null);
  const simulationIntervalRef = useRef(null);
  const telemetryIntervalRef = useRef(null);
  const simulationPauseTimeRef = useRef(0);
  const simulationPauseStartRef = useRef(null);
  const lastTelemetryTimeRef = useRef(0);
  // Note: liveAgentUnsubscribesRef is no longer needed - managed by interface
  const agentStatesRef = useRef([]);
  const liveAgentsRef = useRef([]);
  const uploadEnabledRef = useRef(false);
  const downloadEnabledRef = useRef(false);
  const simulationRunningRef = useRef(false);
  const simulationIdRef = useRef(null);
  // uid -> { position, at }: what this run last wrote, so an unchanged agent is not rewritten.
  const lastSentRef = useRef(new window.Map());
  const simulationClockRef = useRef(null);
  // Set when the operator opted in at the Run prompt: which live skier the virtual agents
  // follow, and the group they were last moved into. Null means they follow nobody.
  const groupFollowRef = useRef(null);
  const groupSyncInFlightRef = useRef(false);
  const virtualAgentsRef = useRef([]);
  // behaviorKey -> sorted sample index, built once before the run starts.
  const datasetIndexesRef = useRef(new window.Map());
  // Frame-rate channel to the map: the marker layer subscribes and never waits on React.
  const positionSourceRef = useRef({
    states: [],
    listeners: new Set(),
    publish(states) {
      this.states = states;
      this.listeners.forEach((listener) => listener(states));
    },
    subscribe(listener) {
      this.listeners.add(listener);
      listener(this.states);
      return () => this.listeners.delete(listener);
    },
    getStates() { return this.states; },
  });

  // Default center coordinates (Vail, Colorado)
  const defaultCenterLatitude = 39.6061;
  const defaultCenterLongitude = -106.3550;
  
  const [centerLatitude, setCenterLatitude] = useState(defaultCenterLatitude);
  const [centerLongitude, setCenterLongitude] = useState(defaultCenterLongitude);

  // Load scenario data and users
  useEffect(() => {
    const loadData = async () => {
      if (!scenarioId || scenarioId === 'new') {
        // Redirect to scenarios list if trying to access /lvc/new directly
        navigate('/lvc');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const scenario = await fetchScenario(scenarioId);
        const agentUids = [...(scenario.virtual_agents || []), ...(scenario.live_agents || [])].map(a => a.uid);
        const profiles = await fetchUserProfiles(agentUids, addError);
        // An agent whose profile can't be found still runs, labelled by its uid
        const usersData = [
          ...profiles,
          ...[...new Set(agentUids)].filter(uid => uid && !profiles.some(p => p.uid === uid))
            .map(uid => ({ uid, name: uid, email: null, profilePicturePath: null, initials: null }))
        ];
        setVirtualAgents(scenario.virtual_agents || []);
        setLiveAgents(scenario.live_agents || []);
        setUsers(usersData);
        setSkiAreaId(scenario.ski_area_id || null);
        
        // Fetch ski area if ski_area_id is present
        if (scenario.ski_area_id) {
          try {
            const skiAreaData = await fetchSkiAreaById(scenario.ski_area_id);
            setSkiArea(skiAreaData);
          } catch (err) {
            console.error('Failed to fetch ski area:', err);
            // Don't show error to user, just log it - scenario can still work without ski area name
            setSkiArea(null);
          }
        } else {
          setSkiArea(null);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        setError(err.message || 'Failed to load scenario');
        // Don't navigate away immediately - let user see the error
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [scenarioId, navigate, addError]);

  // Set up error callback for interface errors
  useEffect(() => {
    setErrorCallback((message, details, source) => {
      addError(message, details, source);
    });
    
    return () => {
      setErrorCallback(null); // Clear callback on unmount
    };
  }, [addError]);

  // Cleanup on component unmount - ALWAYS ensure cleanup is called
  useEffect(() => {
    return () => {
      // Component is unmounting - ensure interface is cleaned up
      // Note: cleanup is async but we can't await in cleanup function
      cleanupInterface().catch(err => {
        console.error('Error during cleanup on component unmount:', err);
      });
      clearDatasetCache();
    };
  }, []);

  // Handle page unload (navigation, tab close, etc.) - ensure simulation is terminated
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable cleanup on page unload
      // This is a best-effort cleanup - the backend will also timeout stale simulations
      if (simulationRunningRef.current) {
        // Fire and forget - can't wait for response
        cleanupInterface().catch(() => {});
      }
    };

    const handleVisibilityChange = () => {
      // If page becomes hidden and simulation is running, try to cleanup
      // This helps catch cases where beforeunload doesn't fire (mobile browsers)
      if (document.visibilityState === 'hidden' && simulationRunningRef.current) {
        cleanupInterface().catch(() => {});
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Measure header height for sidebar positioning
  useEffect(() => {
    const measureHeader = () => {
      if (headerRef.current) {
        // Use getBoundingClientRect().bottom to get the distance from viewport top
        // This accounts for the App header above this page's header
        const bottom = headerRef.current.getBoundingClientRect().bottom;
        setHeaderHeight(bottom);
      }
    };

    // Measure immediately
    measureHeader();

    // Also measure after a short delay to ensure header is fully rendered
    const timeoutId = setTimeout(measureHeader, 100);

    // Use ResizeObserver to handle dynamic header size changes
    let resizeObserver = null;
    let rafId = null;
    const currentHeaderRef = headerRef.current;
    if (currentHeaderRef && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        // Use requestAnimationFrame to debounce and avoid ResizeObserver loop warnings
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        rafId = requestAnimationFrame(() => {
          measureHeader();
        });
      });
      resizeObserver.observe(currentHeaderRef);
    }

    return () => {
      clearTimeout(timeoutId);
      if (resizeObserver && currentHeaderRef) {
        resizeObserver.unobserve(currentHeaderRef);
      }
      // Cleanup any pending animation frame
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  // Close agent menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openAgentMenu !== null) {
        // Check if click is outside any menu container
        const menuContainer = event.target.closest('.agent-menu-container');
        if (!menuContainer) {
          setOpenAgentMenu(null);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openAgentMenu]);

  // Calculate map center based on ski area geometry using Turf.js
  useEffect(() => {
    const calculateMapCenter = () => {
      if (!skiArea || !skiArea.geometry) {
        // No ski area, use default Vail center
        setCenterLatitude(defaultCenterLatitude);
        setCenterLongitude(defaultCenterLongitude);
        return;
      }

      try {
        // Use Turf.js center function to calculate the center of any GeoJSON geometry
        // This works with Point, Polygon, MultiPolygon, LineString, etc.
        const feature = { type: 'Feature', geometry: skiArea.geometry, properties: {} };
        const centerPoint = center(feature);
        const [lon, lat] = centerPoint.geometry.coordinates;
        
        setCenterLatitude(lat);
        setCenterLongitude(lon);
      } catch (err) {
        console.error('Error calculating geometry center:', err);
        setCenterLatitude(defaultCenterLatitude);
        setCenterLongitude(defaultCenterLongitude);
      }
    };

    calculateMapCenter();
  }, [skiArea, defaultCenterLatitude, defaultCenterLongitude]);

  // Define sidebar tabs
  const sidebarTabs = [
    {
      id: 'virtual',
      label: 'Virtual',
      icon: 'V'
    },
    {
      id: 'live',
      label: 'Live',
      icon: 'L'
    }
  ];

  const handleAddVirtualAgent = async (user) => {
    const updatedAgents = [...virtualAgents, { uid: user.uid }];
    setVirtualAgents(updatedAgents);
    setShowUserSelect(false);
    setSelectingFor(null);
    
    // Auto-save immediately
    if (scenarioId && scenarioId !== 'new') {
      try {
        const scenario = await fetchScenario(scenarioId);
        await updateScenario(scenarioId, {
          virtual_agents: updatedAgents,
          live_agents: scenario.live_agents || [],
          ski_area_id: scenario.ski_area_id || null
        });
      } catch (err) {
        console.error('Failed to auto-save:', err);
        // Don't show alert on auto-save failure, user can manually save
      }
    }
  };

  const handleRemoveVirtualAgent = (index) => {
    setVirtualAgents(virtualAgents.filter((_, i) => i !== index));
  };

  const handleSwapVirtualAgent = async (index, newUser) => {
    const updatedAgents = [...virtualAgents];
    // Keep the existing behaviors but swap the uid
    updatedAgents[index] = { ...updatedAgents[index], uid: newUser.uid };
    setVirtualAgents(updatedAgents);
    setShowUserSelect(false);
    setSelectingFor(null);
    setSwappingAgentIndex(null);

    // Auto-save immediately
    if (scenarioId && scenarioId !== 'new') {
      try {
        const scenario = await fetchScenario(scenarioId);
        await updateScenario(scenarioId, {
          virtual_agents: updatedAgents,
          live_agents: scenario.live_agents || [],
          ski_area_id: scenario.ski_area_id || null
        });
      } catch (err) {
        console.error('Failed to auto-save:', err);
      }
    }
  };

  const handleAddLiveAgent = async (user) => {
    const updatedAgents = [...liveAgents, { uid: user.uid }];
    setLiveAgents(updatedAgents);
    setShowUserSelect(false);
    setSelectingFor(null);
    
    // Auto-save immediately
    if (scenarioId && scenarioId !== 'new') {
      try {
        const scenario = await fetchScenario(scenarioId);
        await updateScenario(scenarioId, {
          virtual_agents: scenario.virtual_agents || [],
          live_agents: updatedAgents,
          ski_area_id: scenario.ski_area_id || null
        });
      } catch (err) {
        console.error('Failed to auto-save:', err);
        // Don't show alert on auto-save failure, user can manually save
      }
    }
  };

  const getUserByUid = useCallback((uid) => {
    return users.find(user => user.uid === uid);
  }, [users]);

  const handleSwapLiveAgent = async (index, newUser) => {
    const agentToRemove = liveAgents[index];
    if (agentToRemove && agentToRemove.uid) {
      // The listener effect keys off `liveAgents`, so dropping the agent unsubscribes it.
      // Clear position from state
      setLiveAgentPositions(prev => {
        const updated = { ...prev };
        delete updated[agentToRemove.uid];
        return updated;
      });
    }
    
    const updatedAgents = [...liveAgents];
    updatedAgents[index] = { uid: newUser.uid };
    setLiveAgents(updatedAgents);
    setShowUserSelect(false);
    setSelectingFor(null);
    setSwappingAgentIndex(null);

    // If simulation is running, we need to update the interface's liveUsers
    // The listener effect subscribes the new agent on its next run.
    // with downloadEnabled=true, but we should ensure the interface knows about it
    // Note: The interface will handle subscription when downloadEnabled is true

    // Auto-save immediately
    if (scenarioId && scenarioId !== 'new') {
      try {
        const scenario = await fetchScenario(scenarioId);
        await updateScenario(scenarioId, {
          virtual_agents: scenario.virtual_agents || [],
          live_agents: updatedAgents,
          ski_area_id: scenario.ski_area_id || null
        });
      } catch (err) {
        console.error('Failed to auto-save:', err);
      }
    }
  };

  const handleRemoveLiveAgent = (index) => {
    const agentToRemove = liveAgents[index];
    if (agentToRemove && agentToRemove.uid) {
      // The listener effect keys off `liveAgents`, so dropping the agent unsubscribes it.
      // Clear position from state
      setLiveAgentPositions(prev => {
        const updated = { ...prev };
        delete updated[agentToRemove.uid];
        return updated;
      });
    }
    setLiveAgents(liveAgents.filter((_, i) => i !== index));
  };

  // Fetch profile pictures for virtual and live agents (shared cache)
  useEffect(() => {
    const fetchAgentProfilePictures = async () => {
      // Combine virtual and live agents
      const allAgents = [
        ...virtualAgents.map(agent => ({ ...agent, type: 'virtual' })),
        ...liveAgents.map(agent => ({ ...agent, type: 'live' }))
      ];
      
      const agentsNeedingPictures = allAgents
        .map(agent => {
          const user = getUserByUid(agent.uid);
          return user && user.profilePicturePath ? { uid: agent.uid, profilePicturePath: user.profilePicturePath } : null;
        })
        .filter(Boolean)
        .filter(({ uid }) => !virtualAgentProfilePictures.has(uid) && !failedProfilePictures.has(uid));

      if (agentsNeedingPictures.length === 0) {
        return;
      }

      const urlPromises = agentsNeedingPictures.map(async ({ uid, profilePicturePath }) => {
        const url = await getProfilePictureUrl(profilePicturePath);
        return { uid, url };
      });

      const results = await Promise.all(urlPromises);
      setVirtualAgentProfilePictures(prevUrls => {
        const newUrls = new window.Map(prevUrls);
        results.forEach(({ uid, url }) => {
          if (url) {
            newUrls.set(uid, url);
          } else {
            setFailedProfilePictures(prev => new Set(prev).add(uid));
          }
        });
        return newUrls;
      });
    };

    if ((virtualAgents.length > 0 || liveAgents.length > 0) && users.length > 0) {
      fetchAgentProfilePictures();
    }
    // Note: virtualAgentProfilePictures and failedProfilePictures are intentionally excluded from dependencies
    // to prevent infinite loops. We use functional setState to access the latest state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [virtualAgents, liveAgents, users, getUserByUid]);


  const handleUserSelect = (user) => {
    // Agents are looked up by uid in `users`, so keep the picked profile there
    setUsers(prev => [...prev.filter(u => u.uid !== user.uid), user]);
    if (selectingFor === 'swap' && swappingAgentIndex !== null) {
      handleSwapVirtualAgent(swappingAgentIndex, user);
    } else if (selectingFor === 'swap-live' && swappingAgentIndex !== null) {
      handleSwapLiveAgent(swappingAgentIndex, user);
    } else if (selectingFor === 'virtual') {
      handleAddVirtualAgent(user);
    } else if (selectingFor === 'live') {
      handleAddLiveAgent(user);
    }
  };

  const getAvailableUids = () => {
    const allUids = [...virtualAgents.map(a => a.uid), ...liveAgents.map(a => a.uid)];
    return allUids.filter(uid => uid); // Filter out undefined/null
  };

  // Get initials from name (first letter of first word + first letter of last word)
  const getInitials = (name) => {
    if (!name) return '?';
    const words = name.trim().split(/\s+/);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0][0].toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  // Calculate behavior count and total minutes for an agent
  const getAgentStats = (agent) => {
    const behaviors = agent.behaviors || [];
    const behaviorCount = behaviors.length;
    
    let totalSeconds = 0;
    behaviors.forEach(behavior => {
      if (behavior.type === 'wait') {
        totalSeconds += (behavior.duration || 0);
      } else if (behavior.type === 'datasetview') {
        if (behavior.start_time && behavior.end_time) {
          totalSeconds += (behavior.end_time - behavior.start_time);
        }
      }
    });
    
    const totalMinutes = (totalSeconds / 60).toFixed(1);
    
    return { behaviorCount, totalMinutes };
  };

  // Calculate maximum simulation time based on all agent behaviors
  const getMaxSimulationTime = () => {
    return calculateMaxSimulationTime(virtualAgents);
  };

  const handleSave = async () => {
    if (!scenarioId || scenarioId === 'new') {
      // Can't save a new scenario without a name - should have been created via modal
      alert('Cannot save: Invalid scenario');
      return;
    }

    try {
      setSaving(true);
      setSaveSuccess(false);
      const scenario = await fetchScenario(scenarioId);
      await updateScenario(scenarioId, {
        virtual_agents: virtualAgents,
        live_agents: liveAgents,
        ski_area_id: scenario.ski_area_id || null
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save scenario:', err);
      alert(`Failed to save scenario: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Scenario compiler checks
  const validateScenario = () => {
    setCompilerError(null);

    // Check if there are any virtual agents
    if (virtualAgents.length === 0) {
      setCompilerError('Scenario compiler check failed: No virtual agents found. At least one virtual agent is required to run a simulation.');
      return false;
    }

    // Check if each virtual agent has at least one behavior
    const agentsWithoutBehaviors = virtualAgents.filter((agent) => {
      const behaviors = agent.behaviors || [];
      return behaviors.length === 0;
    });

    if (agentsWithoutBehaviors.length > 0) {
      const agentNames = agentsWithoutBehaviors.map((agent) => {
        const user = getUserByUid(agent.uid);
        return user ? user.name : agent.uid;
      });
      setCompilerError(`Scenario compiler check failed: The following virtual agent(s) have no behaviors: ${agentNames.join(', ')}. Each virtual agent must have at least one behavior.`);
      return false;
    }

    return true;
  };

  // The simulation clock runs on animation frames and writes positions into refs. React only
  // hears about it through onUiUpdate, a few times a second, so the page stops re-rendering
  // at tick rate. Datasets are preloaded before the run, so no fetching happens per frame.
  useEffect(() => {
    if (!simulationRunning || !simulationReady) return void 0;

    const clock = createSimulationClock({
      indexes: datasetIndexesRef.current,
      onFrame: (states) => {
        agentStatesRef.current = states;
        positionSourceRef.current.publish(states);
      },
      onUiUpdate: (states, elapsed) => {
        setAgentStates(states);
        setSimulationCurrentTime(elapsed);
      },
    });

    simulationClockRef.current = clock;
    clock.start(agentStatesRef.current);
    setSimulationStartTime(Date.now());

    return () => {
      clock.stop();
      simulationClockRef.current = null;
    };
  }, [simulationRunning, simulationReady]);

  // Pause and resume the clock rather than tearing it down, so elapsed time is preserved.
  useEffect(() => {
    const clock = simulationClockRef.current;
    if (!clock) return;
    if (simulationPaused) clock.pause();
    else clock.resume();
  }, [simulationPaused]);

  // Keep refs in sync with state
  useEffect(() => {
    simulationRunningRef.current = simulationRunning;
    uploadEnabledRef.current = uploadEnabled && simulationRunning;
    downloadEnabledRef.current = downloadEnabled && simulationRunning;
    liveAgentsRef.current = liveAgents;
    virtualAgentsRef.current = virtualAgents;
  }, [uploadEnabled, downloadEnabled, simulationRunning, liveAgents, virtualAgents]);
  // Telemetry loop - once a second, batch every agent that has data into one Firestore write.
  // Live agents are not polled here; they arrive through onSnapshot listeners (see below).
  useEffect(() => {
    if (!simulationRunning || simulationPaused) {
      lastTelemetryTimeRef.current = 0;
      return void 0;
    }

    const telemetryInterval = setInterval(() => {
      if (!simulationRunningRef.current) return;

      const elapsedTime = (Date.now() - simulationStartTime - simulationPauseTimeRef.current) / 1000;
      if (Math.floor(elapsedTime) <= lastTelemetryTimeRef.current) return;
      lastTelemetryTimeRef.current = Math.floor(elapsedTime);

      // Collect this second's reading for every agent that has one. A datasetview agent only
      // counts when the dataset holds a sample at this timestamp; a wait agent always does.
      const pending = [];
      agentStatesRef.current.forEach((agentState) => {
        if (!agentState.hasFirstLocation || !agentState.currentPosition) return;
        const uid = agentState.agent.uid;
        const user = getUserByUid(uid);
        if (!user) return;

        const behaviors = agentState.agent.behaviors || [];
        const currentBehavior = behaviors[agentState.currentBehaviorIndex];
        const position = { ...agentState.currentPosition };
        let stats = null;

        if (currentBehavior && currentBehavior.type === 'datasetview') {
          // The clock resolves the real sample at this instant (currentExactSample); drawn
          // positions are interpolated but uploads must carry a sample that actually exists.
          const sample = agentState.currentExactSample;
          if (!sample) return;
          position.altitude = sample.altitude ?? null;
          position.course = sample.course ?? null;
          position.accuracy = sample.accuracy ?? null;
          position.speed = sample.speed ?? null;
          if (sample.stats) stats = sample.stats;
        }

        pending.push({ uid, user, position, stats, simTimestamp: Math.floor(elapsedTime) });
      });

      if (pending.length === 0) return;

      // Feature detection per agent, for the map and the details panel.
      pending.forEach(({ uid, user, position, stats, simTimestamp }) => {
        const skierDataModel = createSkierDataModel({
          location: {
            lat: position.lat,
            lon: position.lon,
            timestamp: simTimestamp,
            altitude: position.altitude ?? null,
            speed: position.speed ?? null,
            course: position.course ?? null,
            accuracy: position.accuracy ?? null,
            verticalAccuracy: null,
            speedAccuracy: null
          },
          stats
        });
        onVirtualAgentTick(user, skierDataModel).then((result) => {
          setDetectedFeatures((prev) => {
            if (result && result.detectedFeature) return { ...prev, [uid]: result.detectedFeature };
            if (!(uid in prev)) return prev;
            const updated = { ...prev };
            delete updated[uid];
            return updated;
          });
        }).catch((err) => console.error('Feature detection failed:', err));
      });

      // One batched write for every agent whose reading actually changed.
      if (!uploadEnabledRef.current) return;
      uploadTick(
        pending.map(({ uid, position, stats, simTimestamp }) => ({
          uid,
          position: {
            lat: position.lat,
            lon: position.lon,
            altitude: position.altitude ?? 0,
            speed: position.speed ?? 0,
            heading: position.course ?? 0,
            accuracy: position.accuracy ?? 0,
            simTimestamp
          },
          stats
        })),
        { simulationId: simulationIdRef.current, lastSent: lastSentRef.current }
      ).catch((err) => {
        console.error('Simulation upload failed:', err);
        addError('Failed to upload simulation telemetry', err.message || String(err), 'LVC');
      });
    }, 1000);

    telemetryIntervalRef.current = telemetryInterval;
    return () => {
      if (telemetryIntervalRef.current) {
        clearInterval(telemetryIntervalRef.current);
        telemetryIntervalRef.current = null;
      }
    };
  }, [simulationRunning, simulationPaused, simulationStartTime, getUserByUid, addError]);

  // The live skier can change group in the app mid-run. The virtual agents follow them, which
  // is the whole point of having joined. A skier who goes solo is not followed: there is no
  // group to move into, and the agents keep the last one.
  const syncGroupWithLiveSkier = useCallback(async (liveUid, skiGroup) => {
    const follow = groupFollowRef.current;
    if (!follow || follow.liveUid !== liveUid) return;
    if (!skiGroup || skiGroup === follow.groupId) return;
    if (groupSyncInFlightRef.current) return;

    groupSyncInFlightRef.current = true;
    const previousGroupId = follow.groupId;
    groupFollowRef.current = { ...follow, groupId: skiGroup };
    try {
      const uids = virtualAgentsRef.current.map((agent) => agent.uid).filter(Boolean);
      const { moved } = await moveAgentsToGroup(uids, skiGroup);
      console.log(`[LVC] live skier moved to ${skiGroup}; followed with ${moved.length} agent(s)`);
    } catch (err) {
      console.error('Failed to follow the live skier into their new ski group:', err);
      groupFollowRef.current = { ...follow, groupId: previousGroupId };
      addError('The virtual agents did not follow the live skier into their new ski group', err.message || String(err), 'LVC');
    } finally {
      groupSyncInFlightRef.current = false;
    }
  }, [addError]);

  // Live agents: one onSnapshot listener per skiers/{uid}. Firestore denies `list`, so this
  // is per document. The puck moves when the skier moves instead of once a second.
  useEffect(() => {
    if (!simulationRunning || !downloadEnabled) return void 0;

    const unsubscribes = liveAgents
      .filter((agent) => agent.uid)
      .map((agent) => subscribeLiveAgent(agent.uid, (data) => {
        if (!data) return;
        const entry = {
          lat: data.lat,
          lon: data.lon,
          altitude: data.altitude ?? null,
          speed: data.speed ?? null,
          heading: data.heading ?? null,
          timestamp: data.timestamp ? new Date(data.timestamp).getTime() : Date.now(),
          stats: data.stats && data.stats.lastUpdate ? data.stats : null
        };
        setLiveAgentPositions((prev) => ({ ...prev, [agent.uid]: entry }));
        setLiveAgentHistory((prev) => ({ ...prev, [agent.uid]: [...(prev[agent.uid] || []), entry] }));
        syncGroupWithLiveSkier(agent.uid, data.skiGroup);
      }, (err) => addError(`Live agent ${agent.uid} stopped updating`, err.message || String(err), 'LVC')));

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [simulationRunning, downloadEnabled, liveAgents, addError, syncGroupWithLiveSkier]);


  // Live agents that are in a ski group, newest read, with the group's display name.
  const findSkiGroupCandidates = async () => {
    const uids = liveAgents.map((agent) => agent.uid).filter(Boolean);
    if (uids.length === 0 || virtualAgents.length === 0) return [];

    try {
      const groups = await readSkiGroups(uids);
      const inGroup = uids.filter((uid) => groups.get(uid));
      const named = await Promise.all(inGroup.map(async (uid) => {
        const groupId = groups.get(uid);
        let groupName = null;
        try {
          const details = await getSkiGroupDetails(groupId);
          groupName = details?.name || null;
        } catch (err) {
          console.error(`[lvc] could not read ski group ${groupId}:`, err);
        }
        const user = getUserByUid(uid);
        return { uid, name: user?.name || uid, groupId, groupName };
      }));
      return named;
    } catch (err) {
      console.error('Failed to read live skier ski groups:', err);
      return [];
    }
  };

  const startSimulation = async (joinGroupId, joinLiveUid = null) => {
    // Generate unique simulation ID
        const simulationId = `sim_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        simulationIdRef.current = simulationId;
        lastSentRef.current = new window.Map();
        
        // Convert agent arrays to User arrays
        const virtualUsers = virtualAgents
          .map(agent => getUserByUid(agent.uid))
          .filter(user => user !== void 0);
        
        const liveUsers = liveAgents
          .map(agent => getUserByUid(agent.uid))
          .filter(user => user !== void 0);

        // Ensure cleanup is ALWAYS called, even on errors
        try {
          setSimulationRunning(true);
          setSimulationPaused(false);
          setSimulationStartTime(Date.now());
          setSimulationCurrentTime(0);
          setCompilerError(null);
          setUploadEnabled(false);
          setDownloadEnabled(false);
          simulationPauseTimeRef.current = 0;
          simulationPauseStartRef.current = null;
          
          // Initialize interface with User arrays (not agent objects)
          // This creates references for virtual agents and sets up subscribers for live agents
          // If another simulation is running, it will be canceled automatically
          // Initialize interface with ski area context (if set)
          // Use skiAreaId as source of truth.
          await initializeInterface(virtualUsers, liveUsers, simulationId, { skiAreaId });

          // Put the virtual agents in the live skier's group before any telemetry goes out,
          // so the group is already right when the first positions land.
          if (joinGroupId) {
            groupFollowRef.current = { liveUid: joinLiveUid, groupId: joinGroupId };
            try {
              const { moved, alreadyIn, missing } = await moveAgentsToGroup(
                virtualUsers.map((user) => user.uid),
                joinGroupId
              );
              console.log(`[LVC] ski group ${joinGroupId}: moved ${moved.length}, already in ${alreadyIn.length}, skipped ${missing.length}`);
              if (missing.length > 0) {
                addError(`${missing.length} virtual agent(s) have no skier record, so they were not added to the ski group`, missing.join(', '), 'LVC');
              }
            } catch (err) {
              console.error('Failed to move virtual agents into the ski group:', err);
              addError('Could not move the virtual agents into the ski group', err.message || String(err), 'LVC');
            }
          }

          // Load every datasetview behavior up front. Fetching inside the tick used to start
          // the same download again on each tick and let late responses overwrite newer
          // positions, which is what made agents jump backwards.
          datasetIndexesRef.current = await preloadBehaviors(virtualAgents, addError);

          const initialStates = stepAgents(initializeAgentStates(virtualAgents), 0, datasetIndexesRef.current, new window.Map());
          setAgentStates(initialStates);
          agentStatesRef.current = initialStates;
          positionSourceRef.current.publish(initialStates);
          setSimulationReady(true);
        } catch (err) {
          console.error('Error starting simulation:', err);
          setCompilerError(err.message || 'Failed to start simulation');
          
          // ALWAYS cleanup on error
          cleanupInterface().catch(cleanupError => {
            console.error('Error during cleanup after simulation error:', cleanupError);
          });
          
          // Reset state
          setSimulationRunning(false);
          setSimulationReady(false);
          setSimulationPaused(false);
          setSimulationCurrentTime(0);
          setSimulationStartTime(null);
          setAgentStates([]);
          agentStatesRef.current = [];
          setDetectedFeatures({});
        }
  };

  const handleRunSimulation = async () => {
    if (simulationRunning) {
      // Cancel simulation - STOP ALL WRITES IMMEDIATELY
      // First, set simulation running ref to false to stop any pending writes
      simulationRunningRef.current = false;
      
      // Disable upload/download refs to stop any pending writes
      uploadEnabledRef.current = false;
      downloadEnabledRef.current = false;
      
      // Clear telemetry interval FIRST to stop any pending telemetry updates
      if (telemetryIntervalRef.current) {
        clearInterval(telemetryIntervalRef.current);
        telemetryIntervalRef.current = null;
      }
      
      // Clear simulation interval
      if (simulationIntervalRef.current) {
        clearInterval(simulationIntervalRef.current);
        simulationIntervalRef.current = null;
      }
      
      // Update state
      setSimulationRunning(false);
      setSimulationReady(false);
      setSimulationPaused(false);
      setSimulationCurrentTime(0);
      setSimulationStartTime(null);
      setAgentStates([]);
      agentStatesRef.current = [];
      setCompilerError(null);
      setSelectedAgentIndex(null);
      setUploadEnabled(false);
      setDownloadEnabled(false);
      setDetectedFeatures({});
      simulationPauseTimeRef.current = 0;
      simulationPauseStartRef.current = null;
      lastTelemetryTimeRef.current = 0;
      
      // Clear live agent selections and history
      setSelectedLiveAgentUid(null);
      setLiveAgentHistory({});

      // A simulated skier is a skier: what the run wrote stands afterwards, exactly as a real
      // ski day would leave it. Nothing is reverted on stop.
      lastSentRef.current = new window.Map();
      simulationIdRef.current = null;
      groupFollowRef.current = null;

      // ALWAYS cleanup interface (terminates simulation on backend)
      cleanupInterface().catch(err => {
        console.error('Error during interface cleanup:', err);
      });
      setLiveAgentPositions({});
    } else {
      // Run simulation - validate first
      if (!validateScenario()) return;

      // A live skier already in a ski group can take the virtual agents with them. The live
      // user owns that group; this only ever moves virtual agents in. Their document is not
      // being watched yet, so read it directly.
      const candidates = await findSkiGroupCandidates();
      if (candidates.length > 0) {
        setSkiGroupPrompt({ candidates, agentCount: virtualAgents.length });
        return;
      }
      await startSimulation(null);
    }
  };

  const handlePlayPause = () => {
    if (simulationRunning) {
      setSimulationPaused(!simulationPaused);
    }
  };

  return (
    <div className="lvc-scenario-page">
      <header ref={headerRef} className="lvc-scenario-header">
        <div className="lvc-scenario-header-content">
          <div className="header-left">
            <Button
              variant="outline"
              size="medium"
              onClick={() => navigate('/lvc')}
              className="back-button"
            >
              ←
            </Button>
            <h1>
              {scenarioId && scenarioId !== 'new' 
                ? skiArea 
                  ? `${scenarioId} at ${skiArea.name}`
                  : `${scenarioId} at Unlisted Ski Resort`
                : 'LVC Scenario'}
            </h1>
          </div>
          <div className="header-right">
            {scenarioId && scenarioId !== 'new' && (
              <div className="header-actions">
                <Button
                  variant="primary"
                  size="medium"
                  onClick={handleSave}
                  disabled={saving || loading || simulationRunning}
                >
                  {saving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save'}
                </Button>
                <Button
                  variant={simulationRunning ? "danger" : "success"}
                  size="medium"
                  onClick={handleRunSimulation}
                  disabled={loading || saving}
                >
                  {simulationRunning ? 'Cancel Simulation' : 'Run Simulation'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>
      <main className="lvc-scenario-main" style={{ position: 'relative' }}>
        {error && (
          <div className="scenario-error">
            <p>Error: {error}</p>
            <Button
              variant="outline"
              size="small"
              onClick={() => navigate('/lvc')}
            >
              Back to Scenarios
            </Button>
          </div>
        )}
        {compilerError && (
          <div className="compiler-error">
            <p>{compilerError}</p>
          </div>
        )}
        {loading && !error && (
          <div className="scenario-loading">
            <p>Loading scenario...</p>
          </div>
        )}
        {!loading && !error && (
          <>
            <div className="lvc-scenario-map-container">
              <Map 
                height="100%" 
                positionSource={simulationRunning ? positionSourceRef.current : null}
                locations={[]}
                currentTime={simulationRunning ? simulationCurrentTime : null}
                centerLatitude={centerLatitude}
                centerLongitude={centerLongitude}
                agentPositions={simulationRunning ? [
                  // Virtual agents
                  ...agentStates.map((state, index) => {
                    const user = getUserByUid(state.agent.uid);
                    const displayName = user?.name || state.agent.uid;
                    const hasValidPosition = state.currentPosition && 
                      state.currentPosition.lat !== null && 
                      state.currentPosition.lat !== void 0 &&
                      state.currentPosition.lon !== null && 
                      state.currentPosition.lon !== void 0;
                    return {
                      id: `virtual-${index}`,
                      lat: state.currentPosition?.lat,
                      lon: state.currentPosition?.lon,
                      visible: hasValidPosition,
                      name: displayName,
                      initials: getInitials(displayName),
                      index: index,
                      isLive: false,
                      profilePicturePath: user?.profilePicturePath || null
                    };
                  }),
                  // Live agents
                  ...liveAgents.map((agent, index) => {
                    const user = getUserByUid(agent.uid);
                    const displayName = user?.name || agent.uid;
                    const position = liveAgentPositions[agent.uid];
                    return {
                      id: `live-${agent.uid}`,
                      lat: position?.lat,
                      lon: position?.lon,
                      visible: position && position.lat !== null && position.lon !== null,
                      name: displayName,
                      initials: getInitials(displayName),
                      index: agentStates.length + index, // Offset index for live agents
                      isLive: true,
                      profilePicturePath: user?.profilePicturePath || null
                    };
                  })
                ] : []}
                onAgentClick={(agentIndex) => {
                  if (simulationRunning && agentIndex !== null) {
                    // Check if it's a live agent (index >= agentStates.length)
                    if (agentIndex >= agentStates.length) {
                      // It's a live agent
                      const liveAgentIndex = agentIndex - agentStates.length;
                      const liveAgent = liveAgents[liveAgentIndex];
                      if (liveAgent) {
                        setSelectedLiveAgentUid(liveAgent.uid);
                        setSelectedAgentIndex(null); // Clear virtual agent selection
                      }
                    } else {
                      // It's a virtual agent
                      setSelectedAgentIndex(agentIndex);
                      setSelectedLiveAgentUid(null); // Clear live agent selection
                    }
                    setActiveTab('virtual'); // Ensure virtual tab is open
                  }
                }}
              />
              <Timeline
                locations={[]}
                currentTime={simulationRunning ? simulationCurrentTime : null}
                onTimeChange={(time) => {
                  if (simulationRunning) {
                    // When scrubbing, pause simulation and jump to time
                    const wasPlaying = !simulationPaused;
                    if (wasPlaying) {
                      setSimulationPaused(true);
                    }
                    
                    // Move the clock itself; it recomputes positions and republishes.
                    setSimulationCurrentTime(time);
                    const clock = simulationClockRef.current;
                    if (clock) {
                      // Time jumped, so what was last written no longer says anything about
                      // what should be written next.
                      lastSentRef.current = new window.Map();
                      lastTelemetryTimeRef.current = Math.floor(time) - 1;
                      const seeked = clock.seek(time);
                      setAgentStates(seeked);
                      agentStatesRef.current = seeked;
                    }
                    
                    // Adjust simulationStartTime so that when we resume, elapsed time calculation
                    // will give us the scrubbed time
                    // elapsedTime = (now - simulationStartTime - pauseTime) / 1000
                    // We want: time = (now - newStartTime - pauseTime) / 1000
                    // So: newStartTime = now - (time * 1000) - pauseTime
                    const now = Date.now();
                    
                    // Reset pause time since we're jumping to a new position
                    // The pause time accumulated so far doesn't apply to the new position
                    simulationPauseTimeRef.current = 0;
                    simulationPauseStartRef.current = null;
                    
                    // Calculate new start time so elapsed time equals the scrubbed time
                    const newStartTime = now - (time * 1000);
                    setSimulationStartTime(newStartTime);
                    
                    // If we were playing, resume after a brief moment
                    if (wasPlaying) {
                      // Small delay to ensure state updates are processed
                      setTimeout(() => {
                        setSimulationPaused(false);
                      }, 50);
                    }
                  }
                }}
                isPlaying={simulationRunning && !simulationPaused}
                onPlayPause={handlePlayPause}
                disabled={!simulationRunning}
                sidebarOpen={activeTab !== null}
                sidebarWidth={sidebarWidth}
                minTime={0}
                maxTime={getMaxSimulationTime()}
                showSimulationIcons={simulationRunning}
                simulationRunning={simulationRunning}
                uploadEnabled={uploadEnabled}
                downloadEnabled={downloadEnabled}
                onUpload={() => {
                  setUploadEnabled(!uploadEnabled);
                }}
                onDownload={() => {
                  setDownloadEnabled(!downloadEnabled);
                }}
              />
            </div>
        <Sidebar
          tabs={sidebarTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          width={sidebarWidth}
          onWidthChange={setSidebarWidth}
          topOffset={headerHeight}
        >
          {activeTab === 'virtual' && (
            <div className="virtual-panel">
              {selectedAgentIndex !== null && simulationRunning && agentStates[selectedAgentIndex] ? (
                <AgentDetailsPanel
                  agentState={agentStates[selectedAgentIndex]}
                  simulationCurrentTime={simulationCurrentTime}
                  getUserByUid={getUserByUid}
                  getAgentStats={getAgentStats}
                  detectedFeature={detectedFeatures[agentStates[selectedAgentIndex]?.agent?.uid]}
                  onClose={() => setSelectedAgentIndex(null)}
                />
              ) : selectedLiveAgentUid !== null && simulationRunning ? (
                <LiveAgentDetailsPanel
                  uid={selectedLiveAgentUid}
                  user={getUserByUid(selectedLiveAgentUid)}
                  telemetryHistory={liveAgentHistory[selectedLiveAgentUid] || []}
                  currentPosition={liveAgentPositions[selectedLiveAgentUid]}
                  onClose={() => setSelectedLiveAgentUid(null)}
                />
              ) : (
                <VirtualAgentsPanel
                  agents={virtualAgents}
                  getUserByUid={getUserByUid}
                  getAgentStats={getAgentStats}
                  profilePictures={virtualAgentProfilePictures}
                  failedProfilePictures={failedProfilePictures}
                  openAgentMenu={openAgentMenu}
                  onMenuToggle={setOpenAgentMenu}
                  onAddUser={() => {
                    setSelectingFor('virtual');
                    setShowUserSelect(true);
                  }}
                  onSwap={(index) => {
                    setSwappingAgentIndex(index);
                    setSelectingFor('swap');
                    setShowUserSelect(true);
                    setOpenAgentMenu(null);
                  }}
                  onRemove={(index) => {
                    handleRemoveVirtualAgent(index);
                    setOpenAgentMenu(null);
                  }}
                  onAddBehavior={(index) => navigate(`/lvc/${scenarioId}/agent/${index}`)}
                  onUserClick={() => navigate('/users')}
                  onNameClick={(index) => navigate(`/lvc/${scenarioId}/agent/${index}`)}
                  onProfilePictureError={(uid) => setFailedProfilePictures(prev => new Set(prev).add(uid))}
                  disabled={simulationRunning}
                />
              )}
            </div>
          )}
          {activeTab === 'live' && (
            <div className="live-panel">
              <LiveAgentsPanel
                agents={liveAgents}
                getUserByUid={getUserByUid}
                profilePictures={virtualAgentProfilePictures}
                failedProfilePictures={failedProfilePictures}
                openAgentMenu={openAgentMenu}
                onMenuToggle={setOpenAgentMenu}
                onAddUser={() => {
                  setSelectingFor('live');
                  setShowUserSelect(true);
                }}
                onSwap={(index) => {
                  setSwappingAgentIndex(index);
                  setSelectingFor('swap-live');
                  setShowUserSelect(true);
                  setOpenAgentMenu(null);
                }}
                onRemove={(index) => {
                  handleRemoveLiveAgent(index);
                  setOpenAgentMenu(null);
                }}
                onUserClick={() => navigate('/users')}
                onProfilePictureError={(uid) => setFailedProfilePictures(prev => new Set(prev).add(uid))}
                disabled={simulationRunning}
              />
            </div>
          )}
        </Sidebar>
          </>
        )}
      </main>
      <UserSelectModal
        isOpen={showUserSelect}
        onClose={() => {
          setShowUserSelect(false);
          setSelectingFor(null);
        }}
        onSelect={handleUserSelect}
        excludeUids={getAvailableUids()}
      />

      <SkiGroupJoinPrompt
        isOpen={skiGroupPrompt !== null}
        candidates={skiGroupPrompt?.candidates || []}
        agentCount={skiGroupPrompt?.agentCount || 0}
        onConfirm={(groupId, liveUid) => {
          setSkiGroupPrompt(null);
          startSimulation(groupId, liveUid);
        }}
        onSkip={() => {
          setSkiGroupPrompt(null);
          startSimulation(null);
        }}
      />
    </div>
  );
};

export default LVCScenarioPage;
